/**
 * Service-worker registration + push-subscription helpers.
 *
 * Phase 2 ships:
 *   - registerServiceWorker() — call once on app start
 *   - askForNotificationPermission() — gentle, user-initiated
 *   - subscribePush() — register a VAPID web push subscription with backend
 *   - syncGrantedPushSubscription() — repair backend registration on boot
 *   - registerNtfy() — fallback for browsers / OS that throttle web push
 */

const VAPID_PUBLIC_KEY = (typeof import.meta !== 'undefined'
  ? (import.meta as ImportMeta & { env: Record<string, string> }).env?.VITE_VAPID_PUBLIC_KEY
  : undefined) || ''

/** Set by registerServiceWorker when a new SW version is detected and waiting.
 * The Settings page (and a top-of-app toast) read this to prompt Mark to
 * refresh. ``applyUpdate()`` activates the waiting SW + reloads the page. */
let _waitingSW: ServiceWorker | null = null
let _waitingCacheName: string | null = null
const _updateListeners = new Set<(available: boolean) => void>()
let _eventsBound = false
let _prepareUpdateRun = 0
let _reloadScheduled = false
let _controllerSeen = (
  typeof navigator !== 'undefined'
  && 'serviceWorker' in navigator
  && !!navigator.serviceWorker.controller
)

type ApplyingUpdateRecord = {
  scriptURL: string
  cacheName: string | null
  startedAt: number
  attempts: number
}

const APPLYING_UPDATE_KEY = 'agentchat:sw-update:applying'
const APPLYING_UPDATE_TTL_MS = 2 * 60_000
const APPLYING_UPDATE_MAX_AUTO_ATTEMPTS = 3
let _applyingUpdate: ApplyingUpdateRecord | null = null

function rememberAutoReload(cacheName: string): boolean {
  try {
    const key = `agentchat:auto-update:${cacheName}`
    if (window.sessionStorage.getItem(key)) return false
    window.sessionStorage.setItem(key, String(Date.now()))
  } catch {
    // Storage can be blocked; still prefer getting Mark onto the live shell.
  }
  return true
}

function workerScriptURL(sw: ServiceWorker | null): string {
  return sw?.scriptURL || 'unknown'
}

function parseApplyingUpdate(raw: string | null): ApplyingUpdateRecord | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as Partial<ApplyingUpdateRecord>
    if (
      typeof value.scriptURL !== 'string'
      || typeof value.startedAt !== 'number'
      || typeof value.attempts !== 'number'
    ) {
      return null
    }
    return {
      scriptURL: value.scriptURL,
      cacheName: typeof value.cacheName === 'string' ? value.cacheName : null,
      startedAt: value.startedAt,
      attempts: value.attempts,
    }
  } catch {
    return null
  }
}

function writeApplyingUpdate(record: ApplyingUpdateRecord | null): void {
  _applyingUpdate = record
  try {
    if (record) {
      window.sessionStorage.setItem(APPLYING_UPDATE_KEY, JSON.stringify(record))
    } else {
      window.sessionStorage.removeItem(APPLYING_UPDATE_KEY)
    }
  } catch {
    // Storage can be blocked; the in-memory record still protects this page.
  }
}

function readApplyingUpdate(): ApplyingUpdateRecord | null {
  const stored = (() => {
    try { return parseApplyingUpdate(window.sessionStorage.getItem(APPLYING_UPDATE_KEY)) } catch { return null }
  })()
  const record = stored || _applyingUpdate
  if (!record) return null
  if (Date.now() - record.startedAt > APPLYING_UPDATE_TTL_MS) {
    writeApplyingUpdate(null)
    return null
  }
  _applyingUpdate = record
  return record
}

function sameApplyingUpdate(
  record: ApplyingUpdateRecord | null,
  sw: ServiceWorker | null,
  cacheName?: string | null,
): boolean {
  if (!record || !sw) return false
  if (record.scriptURL !== workerScriptURL(sw)) return false
  return !record.cacheName || !cacheName || record.cacheName === cacheName
}

function rememberApplyingUpdate(sw: ServiceWorker, cacheName?: string | null): ApplyingUpdateRecord {
  const existing = readApplyingUpdate()
  const record: ApplyingUpdateRecord = existing && sameApplyingUpdate(existing, sw, cacheName)
    ? { ...existing, attempts: existing.attempts + 1 }
    : {
        scriptURL: workerScriptURL(sw),
        cacheName: cacheName || null,
        startedAt: Date.now(),
        attempts: 1,
      }
  writeApplyingUpdate(record)
  return record
}

function clearApplyingUpdate(sw?: ServiceWorker | null, cacheName?: string | null): void {
  const record = readApplyingUpdate()
  if (!record) return
  if (sw && !sameApplyingUpdate(record, sw, cacheName)) return
  writeApplyingUpdate(null)
}

function notifyUpdateAvailability(available: boolean): void {
  _updateListeners.forEach((cb) => cb(available))
}

function announceUpdate(sw?: ServiceWorker | null, cacheName?: string | null): void {
  if (sw) {
    if (sameApplyingUpdate(readApplyingUpdate(), sw, cacheName)) return
    _waitingSW = sw
    _waitingCacheName = cacheName || null
  }
  notifyUpdateAvailability(true)
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function trackInstallingWorker(worker: ServiceWorker | null, cacheName?: string | null): void {
  if (!worker) return
  if (worker.state === 'installed') {
    if (navigator.serviceWorker.controller) announceUpdate(worker, cacheName)
    return
  }
  worker.addEventListener('statechange', () => {
    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
      announceUpdate(worker, cacheName)
    }
  })
}

export function onUpdateAvailable(cb: (available: boolean) => void): () => void {
  _updateListeners.add(cb)
  // Fire immediately if we already detected an update.
  if (_waitingSW) cb(true)
  return () => _updateListeners.delete(cb)
}

function reloadWithCacheBust(): void {
  if (typeof location === 'undefined') return
  if (_reloadScheduled) return
  _reloadScheduled = true
  const next = new URL(location.href, location.origin)
  next.searchParams.set('agentchat_update', String(Date.now()))
  location.replace(next.toString())
}

function waitForWaitingWorker(
  reg: ServiceWorkerRegistration,
  candidate?: ServiceWorker | null,
  timeoutMs = 8000,
): Promise<ServiceWorker | null> {
  const ready = reg.waiting || (candidate?.state === 'installed' ? candidate : null)
  if (ready) return Promise.resolve(ready)

  const installing = reg.installing || (candidate?.state === 'installing' ? candidate : null)
  if (!installing) return Promise.resolve(null)

  return new Promise((resolve) => {
    let done = false
    const finish = (worker: ServiceWorker | null) => {
      if (done) return
      done = true
      clearTimeout(timeout)
      installing.removeEventListener('statechange', onStateChange)
      resolve(worker)
    }
    const onStateChange = () => {
      if (reg.waiting) {
        finish(reg.waiting)
        return
      }
      if (installing.state === 'installed') {
        finish(installing)
        return
      }
      if (installing.state === 'redundant') {
        finish(null)
      }
    }
    const timeout = window.setTimeout(() => finish(reg.waiting || null), timeoutMs)
    installing.addEventListener('statechange', onStateChange)
    onStateChange()
  })
}

async function readyWaitingWorker(timeoutMs = 8000): Promise<ServiceWorker | null> {
  if (!('serviceWorker' in navigator)) return null
  const reg = await navigator.serviceWorker.getRegistration('/').catch(() => null)
  if (!reg) return null
  if (reg.waiting) return reg.waiting
  if (_waitingSW?.state === 'installed') return _waitingSW
  const updating = await reg.update().catch(() => reg)
  const waiting = updating.waiting || reg.waiting
  if (waiting) return waiting
  const installing = updating.installing || reg.installing || _waitingSW
  if (installing?.state === 'installing' || installing?.state === 'installed') {
    return waitForWaitingWorker(updating, installing, timeoutMs)
  }
  return null
}

async function activateWaitingWorker(waiting: ServiceWorker, timeoutMs = 15_000): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let done = false
    const finish = (changed: boolean) => {
      if (done) return
      done = true
      clearTimeout(timeout)
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
      waiting.removeEventListener('statechange', onStateChange)
      resolve(changed)
    }
    const onControllerChange = () => finish(true)
    const onStateChange = () => {
      if (waiting.state === 'activated') finish(true)
      if (waiting.state === 'redundant') finish(false)
    }
    const timeout = window.setTimeout(() => finish(false), timeoutMs)
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
    waiting.addEventListener('statechange', onStateChange)
    try {
      waiting.postMessage({ type: 'SKIP_WAITING' })
      onStateChange()
    } catch {
      finish(false)
    }
  })
}

async function resumeAppliedWaitingWorker(reg: ServiceWorkerRegistration): Promise<boolean> {
  if (!navigator.serviceWorker.controller || !reg.waiting) return false
  const record = readApplyingUpdate()
  if (!sameApplyingUpdate(record, reg.waiting, _waitingCacheName)) return false

  _waitingSW = reg.waiting
  _waitingCacheName = record?.cacheName || _waitingCacheName
  notifyUpdateAvailability(false)

  if ((record?.attempts || 0) >= APPLYING_UPDATE_MAX_AUTO_ATTEMPTS) {
    console.warn('AgentChat service-worker update is still waiting after repeated activation attempts.')
    return true
  }

  rememberApplyingUpdate(reg.waiting, _waitingCacheName)
  await activateWaitingWorker(reg.waiting)
  reloadWithCacheBust()
  return true
}

export function reloadAfterServiceWorkerActivation(cacheName?: string): void {
  const key = `activated:${cacheName || navigator.serviceWorker.controller?.scriptURL || 'latest'}`
  if (!rememberAutoReload(key)) return
  clearApplyingUpdate(null, cacheName)
  _waitingSW = null
  _waitingCacheName = null
  notifyUpdateAvailability(false)
  reloadWithCacheBust()
}

export function prepareUpdate(cacheName?: string | null): void {
  const run = ++_prepareUpdateRun
  void (async () => {
    const key = cacheName || 'latest'
    const retryDelays = [0, 1200, 3000, 8000]
    for (const waitMs of retryDelays) {
      if (run !== _prepareUpdateRun) return
      if (waitMs > 0) await delay(waitMs)
      const waiting = await readyWaitingWorker()
      if (waiting) {
        _waitingSW = waiting
        _waitingCacheName = cacheName || null
        rememberAutoReload(`prepared:${key}:${waiting.scriptURL}`)
        announceUpdate(waiting, cacheName)
        return
      }
    }
  })()
}

export async function applyUpdate(): Promise<void> {
  const waiting = await readyWaitingWorker()
  if (waiting) {
    // Activate only after Mark asks for the update. This keeps the old SW
    // cache alive while the old React bundle is still running, avoiding the
    // classic "new SW deleted the chunks this tab still needs" failure.
    rememberApplyingUpdate(waiting, _waitingCacheName)
    _waitingSW = null
    notifyUpdateAvailability(false)
    const activated = await activateWaitingWorker(waiting)
    if (!activated) {
      reloadWithCacheBust()
      return
    }
    clearApplyingUpdate(waiting, _waitingCacheName)
  } else if ('serviceWorker' in navigator) {
    reloadWithCacheBust()
    return
  }
  // Use a one-shot URL change rather than location.reload(); it bypasses
  // browser HTTP cache even on older Android WebView/TWA surfaces.
  reloadWithCacheBust()
}

export async function checkForUpdate(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false
  const reg = await navigator.serviceWorker.getRegistration('/')
  if (!reg) return false
  const updated = await reg.update().catch(() => reg)
  const waiting = await waitForWaitingWorker(updated, updated.waiting || updated.installing)
  if (waiting) {
    announceUpdate(waiting, null)
    return true
  }
  trackInstallingWorker(updated.installing || reg.installing)
  return false
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
    if (!_eventsBound) {
      _eventsBound = true
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!_controllerSeen) {
          _controllerSeen = true
          return
        }
        reloadAfterServiceWorkerActivation(navigator.serviceWorker.controller?.scriptURL)
      })
      navigator.serviceWorker.addEventListener('message', (event) => {
        const type = event.data?.type
        if (type === 'AGENTCHAT_RELOAD_REQUIRED') {
          prepareUpdate(typeof event.data?.cacheName === 'string' ? event.data.cacheName : null)
          return
        }
        if (type === 'AGENTCHAT_SW_ACTIVATED') {
          if (!_controllerSeen) {
            _controllerSeen = true
            return
          }
          reloadAfterServiceWorkerActivation(event.data?.cacheName)
        }
      })
    }

    // Detect updates: when a new SW finishes installing while the old one is
    // still controlling the page, surface the prompt.
    if (await resumeAppliedWaitingWorker(reg)) return reg
    // A launch or reload is Mark asking for the current app. Chrome keeps a
    // waiting worker waiting across a plain reload of an open window, so an
    // installed desktop app would otherwise run an old bundle for days.
    // Nothing is in flight this early, so switch before the old shell works.
    if (reg.waiting && navigator.serviceWorker.controller && !readApplyingUpdate()) {
      rememberApplyingUpdate(reg.waiting, null)
      await activateWaitingWorker(reg.waiting)
      reloadWithCacheBust()
      return reg
    }
    if (reg.waiting && navigator.serviceWorker.controller) announceUpdate(reg.waiting)
    if (reg.installing && navigator.serviceWorker.controller) trackInstallingWorker(reg.installing)
    reg.addEventListener('updatefound', () => {
      trackInstallingWorker(reg.installing)
    })
    // Periodic check (every 30 min) so long-running tabs catch updates
    // without a full reload.
    setInterval(() => { reg.update().catch(() => {}) }, 30 * 60_000)
    // Also re-check on tab visibility change.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') reg.update().catch(() => {})
    })
    return reg
  } catch (e) {
    console.warn('SW registration failed:', e)
    return null
  }
}

export async function askForNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied'
  if (Notification.permission === 'granted') return 'granted'
  if (Notification.permission === 'denied') return 'denied'
  return await Notification.requestPermission()
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}

export async function subscribePush(reg: ServiceWorkerRegistration): Promise<unknown> {
  return syncGrantedPushSubscription(reg, { createIfMissing: true, requirePermission: true })
}

async function postPushSubscription(sub: PushSubscription): Promise<unknown> {
  const json = sub.toJSON() as {
    endpoint?: string
    keys?: { p256dh?: string; auth?: string }
  }
  const endpoint = json.endpoint
  const keys = json.keys || {}
  const resp = await fetch('/api/push/subscribe', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      kind: 'webpush',
      endpoint,
      p256dh: keys.p256dh,
      auth: keys.auth,
      device_label: navigator.userAgent.slice(0, 80),
      user_agent: navigator.userAgent,
    }),
  })
  if (!resp.ok) throw new Error(`Push subscribe failed: ${resp.status}`)
  return resp.json()
}

export async function syncGrantedPushSubscription(
  reg: ServiceWorkerRegistration | null,
  opts: { createIfMissing?: boolean; requirePermission?: boolean } = {},
): Promise<unknown | null> {
  if (!reg || !('pushManager' in reg)) return null
  if (!('Notification' in window)) return null
  if (opts.requirePermission) {
    const perm = await askForNotificationPermission()
    if (perm !== 'granted') throw new Error(`Permission ${perm}`)
  } else if (Notification.permission !== 'granted') {
    return null
  }
  if (!VAPID_PUBLIC_KEY) {
    throw new Error('VITE_VAPID_PUBLIC_KEY not configured')
  }
  let sub = await reg.pushManager.getSubscription()
  if (!sub && opts.createIfMissing) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    })
  }
  if (!sub) return null
  return postPushSubscription(sub)
}

export async function registerNtfy(topicUrl: string): Promise<unknown> {
  const resp = await fetch('/api/push/subscribe', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      kind: 'ntfy',
      endpoint: topicUrl,
      device_label: navigator.userAgent.slice(0, 80),
      user_agent: navigator.userAgent,
    }),
  })
  if (!resp.ok) throw new Error(`ntfy register failed: ${resp.status}`)
  return resp.json()
}
